import React, { useEffect, useRef, useState } from 'react';
import { useQueryCache } from 'react-query';
import { usePortalAccess } from '../../../hooks/usePortalAccess';
import { getDirectoryBusinessOptions } from '../../../services/DriverDirectory';
import { portalGeneration } from '../../../services/PortalSession';
import { getDispatcherQueue, getDispatcherAlert } from '../../../services/DispatcherQueue';
import { DeliveryIssueResources } from '../../../services/DeliveryIssueResources';
import DeliveryAlertsPanel from './DeliveryAlertsPanel';

function AuthorizedAlerts({ sessionVersion }) {
  const cache = useQueryCache();
  const [filters, setFilters] = useState({ page: 1 });
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ status: 'loading', data: null, businesses: [] });
  const resources = useRef(null);
  const epoch = useRef(null);
  const stopped = useRef(false);
  const [, redraw] = useState(0);
  const revoke = () => {
    stopped.current = true;
    if (resources.current) resources.current.dispose();
    resources.current = null;
    setState({ status: 'forbidden', data: null, businesses: [] });
  };
  useEffect(() => {
    let alive = true;
    let timer;
    let busy = false;
    let failures = 0;
    const generation = portalGeneration();
    const controller = new AbortController();
    const current = () => alive && generation === portalGeneration();
    setState((old) => ({ ...old, status: stopped.current ? 'forbidden' : 'loading', data: null }));
    const poll = async () => {
      if (!current() || stopped.current || busy || document.hidden) return;
      busy = true;
      clearTimeout(timer);
      try {
        const data = await getDispatcherQueue(filters, controller.signal);
        if (!current() || stopped.current) return;
        if (epoch.current !== data.scope_epoch) {
          if (resources.current) resources.current.dispose();
          resources.current = null;
          epoch.current = data.scope_epoch;
          setState({ status: 'loading', data: null, businesses: [] });
        }
        // Calendar scope comes from the queue, not map selection or directory permissions.
        const businesses = data.business_calendar.map((b) => ({ id: b.business_id, name: `Business #${b.business_id}` }));
        if (!resources.current) {
          resources.current = new DeliveryIssueResources({ businesses, cache, sessionVersion,
            notify: () => { if (current()) redraw((n) => n + 1); } });
        }
        setState({ status: 'success', data, businesses });
        resources.current.resolveNames(data.results.flatMap((g) => g.issues));
        failures = 0;
        // Names are optional; failure must not prevent the authorized queue loading.
        if (!resources.current.businessNames) {
          resources.current.businessNames = getDirectoryBusinessOptions().catch(() => []);
        }
        const activeResources = resources.current;
        activeResources.businessNames.then((names) => {
          if (current() && !stopped.current && resources.current === activeResources) {
            setState((old) => ({ ...old, businesses: businesses.map((b) => ({
              ...b, name: names.find((n) => String(n.id) === String(b.id))?.name || b.name,
            })) }));
          }
        });
      } catch (error) {
        if (!current()) return;
        if (error.status === 403) revoke();
        else {
          failures += 1;
          // Do not leave yesterday's rows visible after a failed calendar refresh.
          setState((old) => ({ ...old, status: 'error', data: null }));
        }
      } finally {
        busy = false;
        if (current() && !stopped.current) timer = setTimeout(poll,
          failures ? Math.min(30000, 5000 * (2 ** (failures - 1))) : 5000);
      }
    };
    poll();
    document.addEventListener('visibilitychange', poll);
    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
      document.removeEventListener('visibilitychange', poll);
      if (resources.current) resources.current.dispose();
      resources.current = null;
    };
  }, [filters, revision, cache, sessionVersion]);

  const detail = async (issue, acknowledge) => {
    try {
      const result = await getDispatcherAlert(issue, acknowledge);
      if (stopped.current) throw new Error('Alert access expired');
      if (result.scope_epoch !== epoch.current) {
        if (resources.current) resources.current.dispose();
        resources.current = null;
        setState({ status: 'loading', data: null, businesses: [] });
        setRevision((n) => n + 1);
        throw new Error('Alert scope changed');
      }
      return result;
    } catch (error) {
      if (error.status === 403) revoke();
      throw error;
    }
  };
  return <DeliveryAlertsPanel queue={{
    ...state, filters,
    setFilters: (next) => setFilters(next),
    detail,
    name: (issue) => resources.current?.name(issue) || `Restaurant #${issue.restaurant_id}`,
    order: async (issue) => {
      try { return await resources.current.order(issue); }
      catch (error) { if (error.status === 403) revoke(); throw error; }
    },
    refresh: () => { if (!stopped.current) setRevision((n) => n + 1); },
  }} />;
}

export default function DeliveryAlerts() {
  const { dispatcher, sessionVersion } = usePortalAccess();
  return dispatcher ? <AuthorizedAlerts key={sessionVersion} sessionVersion={sessionVersion} /> : null;
}
